// 图片上传前的大小校验与压缩工具（基于浏览器原生 Canvas，无额外依赖）
//
// 压缩规则：
// 0. 文件不超过 IMAGE_SKIP_THRESHOLD：无论尺寸多大都原样上传（体积已合理，避免无谓的画质损失）
// 1. 最长边超过 IMAGE_MAX_DIMENSION：等比缩小到该尺寸，并以 IMAGE_QUALITY 导出 JPEG
// 2. 尺寸未超但文件超过 IMAGE_REENCODE_THRESHOLD：保持原尺寸，以 IMAGE_QUALITY 重新编码
// 3. 其余情况原样上传，不重新编码，避免画质二次损失

const MB = 1024 * 1024

// 图片上传大小上限
export const IMAGE_MAX_SIZE = 20 * MB
// 不超过该大小的图片直接原样上传，不做任何处理
export const IMAGE_SKIP_THRESHOLD = 1 * MB
// 尺寸未超限时，超过该大小的图片按原尺寸重新编码
export const IMAGE_REENCODE_THRESHOLD = 2 * MB
// 图片最长边的像素上限，超过则等比缩小
export const IMAGE_MAX_DIMENSION = 1600
// 允许上传的图片类型
export const IMAGE_ACCEPT_TYPES = ['image/jpeg', 'image/png', 'image/webp']
// 压缩导出的 JPEG 质量（固定一档，不再逐级降低，优先保证画质）
export const IMAGE_QUALITY = 0.85

export function formatFileSize (size: number): string {
  return parseFloat((size / MB).toFixed(1)) + 'MB'
}

function loadImage (file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('图片读取失败，请确认文件是否损坏'))
    }
    img.src = url
  })
}

function canvasToBlob (canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality)
  })
}

/**
 * 将已加载的图片按最长边上限等比缩放（不放大），并以固定质量导出为 JPEG。
 * 若导出结果不比原图小，则返回原图。
 */
async function encodeImage (
  file: File,
  img: HTMLImageElement,
  maxDimension: number = IMAGE_MAX_DIMENSION,
  quality: number = IMAGE_QUALITY
): Promise<File> {
  const { naturalWidth: width, naturalHeight: height } = img
  const scale = Math.min(1, maxDimension / Math.max(width, height))

  const canvas = document.createElement('canvas')
  canvas.width = Math.round(width * scale)
  canvas.height = Math.round(height * scale)
  const ctx = canvas.getContext('2d')
  if (!ctx) return file

  // PNG/WebP 可能带透明通道，转 JPEG 前铺白底，避免透明区域变黑
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

  const blob = await canvasToBlob(canvas, quality)

  if (!blob || blob.size >= file.size) return file

  const baseName = file.name.replace(/\.[^.]+$/, '') || 'image'
  return new File([blob], baseName + '.jpg', {
    type: 'image/jpeg',
    lastModified: Date.now()
  })
}

/**
 * 校验并按需压缩图片：
 * - 非允许类型或超过 IMAGE_MAX_SIZE：抛出错误
 * - 不超过 IMAGE_SKIP_THRESHOLD：原样返回
 * - 最长边超过 IMAGE_MAX_DIMENSION：等比缩小并压缩
 * - 尺寸未超但超过 IMAGE_REENCODE_THRESHOLD：按原尺寸重新编码
 * - 其余原样返回
 */
export async function prepareImageForUpload (file: File): Promise<File> {
  if (IMAGE_ACCEPT_TYPES.indexOf(file.type) === -1) {
    throw new Error('仅支持上传 JPG、PNG、WebP 格式的图片')
  }
  if (file.size > IMAGE_MAX_SIZE) {
    throw new Error(`图片大小不能超过 ${formatFileSize(IMAGE_MAX_SIZE)}，当前为 ${formatFileSize(file.size)}`)
  }
  if (file.size <= IMAGE_SKIP_THRESHOLD) {
    return file
  }

  const img = await loadImage(file)
  const longestSide = Math.max(img.naturalWidth, img.naturalHeight)

  if (longestSide > IMAGE_MAX_DIMENSION || file.size > IMAGE_REENCODE_THRESHOLD) {
    return encodeImage(file, img)
  }
  return file
}
